import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { _delete, _get, _put } from "../../../api/chatbotApiClient";
import { API_STATUS } from "../sliceUtil";
import store from "../../../app/store";
import { HttpStatusCode } from "axios";
import { CustomError } from "../../../api/CustomError";
import { CustomSuccess } from "../../../api/CustomSuccess";

export const _orgClientId = () => {
  const state = store.getState();
  return state?.orgReducer?.selectedFI?.orgClientId
    ? state?.orgReducer?.selectedFI?.orgClientId
    : state?.authReducer?.userData?.client_Id;
};

const initialState = {
  history: [],
  bookmark: [],
  deleted: [],
  loading: {
    history: API_STATUS.STATUS_IDLE,
    bookmark: API_STATUS.STATUS_IDLE,
    deleted: API_STATUS.STATUS_IDLE,
  },
};

const getType = (arg = {}) => {
  if (arg.isBookMark) return "bookmark";
  if (arg.isDeletedHistory) return "deleted";
  return "history";
};

export const fetchThreadList = createAsyncThunk(
  "chatbot/fetchThreadList",
  async (
    { isBookMark = false, isDeletedHistory = false, disableToast = false } = {},
    thunkAPI,
  ) => {
    try {
      const config = {
        successMessage: "Fetched thread list successfully.",
        errorMessage: "Failed to fetch thread list!",
        disableToast,
      };

      const response = await _get(
        `${_orgClientId()}/chat?is_bookMark=${isBookMark}&is_deleted_history=${isDeletedHistory}`,
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const {
        data: threadsList,
        error,
        message,
        status,
        statusCode,
        responseType,
      } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });

      const type = getType({ isBookMark, isDeletedHistory });

      return {
        threadsList,
        error,
        message,
        status,
        statusCode,
        responseType,
        type,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const addOrRemoveBookmark = createAsyncThunk(
  "chatbot/addOrRemoveBookmark",
  async ({ threadDetails, disableToast = false }, thunkAPI) => {
    try {
      const config = {
        successMessage: "Thread Updated successfully.",
        errorMessage: "Failed to update Thread!",
        disableToast: disableToast,
      };

      const response = await _put(
        `${_orgClientId()}/chat/bookmark?id=${threadDetails?.threadId}&is_store=${threadDetails?.isBookmark}`,
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      thunkAPI.dispatch(
        fetchThreadList({
          isBookMark: true,
          isDeletedHistory: false,
        }),
      );

      thunkAPI.dispatch(
        fetchThreadList({
          isBookMark: false,
          isDeletedHistory: false,
        }),
      );

      const { error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      return {
        response,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const softdeleteOrRecoverThread = createAsyncThunk(
  "chatbot/softdeleteOrRecoverThread",
  async ({ threadDetails, isDelete, disableToast = false }, thunkAPI) => {
    try {
      const config = {
        successMessage: "Thread Updated successfully.",
        errorMessage: "Failed to update Thread!",
        disableToast: disableToast,
      };

      const response = await _delete(
        `${_orgClientId()}/chat/${threadDetails?.threadId}?is_delete=${isDelete}`,
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      thunkAPI.dispatch(
        fetchThreadList({
          isBookMark: true,
          isDeletedHistory: false,
        }),
      );

      thunkAPI.dispatch(
        fetchThreadList({
          isBookMark: false,
          isDeletedHistory: true,
        }),
      );

      thunkAPI.dispatch(
        fetchThreadList({
          isBookMark: false,
          isDeletedHistory: false,
        }),
      );

      const { error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      return {
        response,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const hardDeleteOrEmptyBin = createAsyncThunk(
  "chatbot/hardDeleteOrEmptyBin",
  async ({ threadDetails, isAllDelete, disableToast = false }, thunkAPI) => {
    try {
      const config = {
        successMessage: "Thread Updated successfully.",
        errorMessage: "Failed to update Thread!",
        disableToast: disableToast,
      };

      const response = await _delete(
        `${_orgClientId()}/chat?id=${threadDetails?.threadId}&is_all_delete=${isAllDelete}`,
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      thunkAPI.dispatch(
        fetchThreadList({
          isBookMark: false,
          isDeletedHistory: true,
        }),
      );

      const { error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      return {
        response,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const renameThreadName = createAsyncThunk(
  "chatbot/renameThreadName",
  async ({ threadDetails, disableToast = false }, thunkAPI) => {
    try {
      const config = {
        successMessage: "Thread name Updated successfully.",
        errorMessage: "Failed to update Thread name!",
        disableToast: disableToast,
      };

      const response = await _put(
        `${_orgClientId()}/chat/${threadDetails?.threadId}`,
        {
          threadName: threadDetails?.threadName,
        },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      thunkAPI.dispatch(
        fetchThreadList({
          isBookMark: false,
          isDeletedHistory: false,
        }),
      );

      thunkAPI.dispatch(
        fetchThreadList({
          isBookMark: true,
          isDeletedHistory: false,
        }),
      );

      const { error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      return {
        response,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const chatbotSidebarToolsSlice = createSlice({
  name: "chatbotSidebar",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchThreadList.pending, (state, action) => {
        const key = getType(action.meta?.arg);

        if (!state.loading) state.loading = {};
        if (!state.loading[key]) state.loading[key] = API_STATUS.STATUS_LOADING;

        state.loading[key] = API_STATUS.STATUS_LOADING;
      })

      .addCase(fetchThreadList.fulfilled, (state, action) => {
        const key = getType(action.meta.arg);

        const list = action.payload?.threadsList;

        const getSafeArray = (list) => {
          if (Array.isArray(list)) return list;
          if (Array.isArray(list?.data)) return list.data;
          return [];
        };

        const safeList = getSafeArray(list);

        state[key] = safeList.map((item) => ({
          ...item,
          type: key,
        }));

        state.loading[key] = API_STATUS.STATUS_SUCCESS;
      })

      .addCase(fetchThreadList.rejected, (state, action) => {
        const key = getType(action.meta?.arg);

        if (!state.loading) state.loading = {};
        state.loading[key] = API_STATUS.STATUS_ERROR;
      });
  },
});
